import { Vector3 } from '../vector/vector3';

export type EulerOrder = 'XYZ' | 'YXZ' | 'ZXY' | 'ZYX' | 'YZX' | 'XZY';

export class Quaternion {
  constructor(
    public x: number,
    public y: number,
    public z: number,
    public w: number,
  ) {}

  static fromEuler(roll: number, pitch: number, yaw: number): Quaternion {
    return Quaternion.fromEulerAngles(roll, pitch, yaw, 'ZYX');
  }

  static fromEulerAngles(x: number, y: number, z: number, order: EulerOrder): Quaternion {
    const c1 = Math.cos(x / 2), s1 = Math.sin(x / 2);
    const c2 = Math.cos(y / 2), s2 = Math.sin(y / 2);
    const c3 = Math.cos(z / 2), s3 = Math.sin(z / 2);

    switch (order) {
      case 'XYZ':
        return new Quaternion(
          s1 * c2 * c3 + c1 * s2 * s3,
          c1 * s2 * c3 - s1 * c2 * s3,
          c1 * c2 * s3 + s1 * s2 * c3,
          c1 * c2 * c3 - s1 * s2 * s3,
        );
      case 'YXZ':
        return new Quaternion(
          s1 * c2 * c3 + c1 * s2 * s3,
          c1 * s2 * c3 - s1 * c2 * s3,
          c1 * c2 * s3 - s1 * s2 * c3,
          c1 * c2 * c3 + s1 * s2 * s3,
        );
      case 'ZXY':
        return new Quaternion(
          s1 * c2 * c3 - c1 * s2 * s3,
          c1 * s2 * c3 + s1 * c2 * s3,
          c1 * c2 * s3 + s1 * s2 * c3,
          c1 * c2 * c3 - s1 * s2 * s3,
        );
      case 'ZYX':
        return new Quaternion(
          s1 * c2 * c3 - c1 * s2 * s3,
          c1 * s2 * c3 + s1 * c2 * s3,
          c1 * c2 * s3 - s1 * s2 * c3,
          c1 * c2 * c3 + s1 * s2 * s3,
        );
      case 'YZX':
        return new Quaternion(
          s1 * c2 * c3 + c1 * s2 * s3,
          c1 * s2 * c3 + s1 * c2 * s3,
          c1 * c2 * s3 - s1 * s2 * c3,
          c1 * c2 * c3 - s1 * s2 * s3,
        );
      case 'XZY':
        return new Quaternion(
          s1 * c2 * c3 - c1 * s2 * s3,
          c1 * s2 * c3 - s1 * c2 * s3,
          c1 * c2 * s3 + s1 * s2 * c3,
          c1 * c2 * c3 + s1 * s2 * s3,
        );
    }
  }

  static multiply(a: Quaternion, b: Quaternion): Quaternion {
    return new Quaternion(
      a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
      a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
      a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
      a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    );
  }

  /** Inverse of {@link Quaternion.fromEuler} (ZYX, radians). */
  static toEuler(q: Quaternion): Vector3 {
    return Quaternion.toEulerAngles(q, 'ZYX');
  }

  /** Euler decomposition for the given intrinsic rotation order (radians). */
  static toEulerAngles(q: Quaternion, order: EulerOrder): Vector3 {
    const m11 = 1 - 2 * (q.y * q.y + q.z * q.z);
    const m12 = 2 * (q.x * q.y - q.w * q.z);
    const m13 = 2 * (q.x * q.z + q.w * q.y);
    const m21 = 2 * (q.x * q.y + q.w * q.z);
    const m22 = 1 - 2 * (q.x * q.x + q.z * q.z);
    const m23 = 2 * (q.y * q.z - q.w * q.x);
    const m31 = 2 * (q.x * q.z - q.w * q.y);
    const m32 = 2 * (q.y * q.z + q.w * q.x);
    const m33 = 1 - 2 * (q.x * q.x + q.y * q.y);

    const lockThreshold = 1 - 1e-7;

    const angles = new Vector3(0, 0, 0);

    switch (order) {
      case 'XYZ':
        angles.y = Math.asin(clamp(m13, -1, 1));
        if (Math.abs(m13) < lockThreshold) {
          angles.x = Math.atan2(-m23, m33);
          angles.z = Math.atan2(-m12, m11);
        } else {
          angles.x = Math.atan2(m32, m22);
          angles.z = 0;
        }
        break;
      case 'YXZ':
        angles.x = Math.asin(-clamp(m23, -1, 1));
        if (Math.abs(m23) < lockThreshold) {
          angles.y = Math.atan2(m13, m33);
          angles.z = Math.atan2(m21, m22);
        } else {
          angles.y = Math.atan2(-m31, m11);
          angles.z = 0;
        }
        break;
      case 'ZXY':
        angles.x = Math.asin(clamp(m32, -1, 1));
        if (Math.abs(m32) < lockThreshold) {
          angles.y = Math.atan2(-m31, m33);
          angles.z = Math.atan2(-m12, m22);
        } else {
          angles.y = 0;
          angles.z = Math.atan2(m21, m11);
        }
        break;
      case 'ZYX':
        angles.y = Math.asin(-clamp(m31, -1, 1));
        if (Math.abs(m31) < lockThreshold) {
          angles.x = Math.atan2(m32, m33);
          angles.z = Math.atan2(m21, m11);
        } else {
          angles.x = 0;
          angles.z = Math.atan2(-m12, m22);
        }
        break;
      case 'YZX':
        angles.z = Math.asin(clamp(m21, -1, 1));
        if (Math.abs(m21) < lockThreshold) {
          angles.x = Math.atan2(-m23, m22);
          angles.y = Math.atan2(-m31, m11);
        } else {
          angles.x = 0;
          angles.y = Math.atan2(m13, m33);
        }
        break;
      case 'XZY':
        angles.z = Math.asin(-clamp(m12, -1, 1));
        if (Math.abs(m12) < lockThreshold) {
          angles.x = Math.atan2(m32, m22);
          angles.y = Math.atan2(m13, m11);
        } else {
          angles.x = Math.atan2(-m23, m33);
          angles.y = 0;
        }
        break;
    }

    return angles;
  }
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
